//Declares API types and functions for audit list/detail; 
// AuditLog includes optional reason so exports can use it 
// when the API returns it.
import { authFetch } from './http';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/v1`
  : '/efx-csp/api/v1';

export interface AuditLog {
  id: number;
  createdOn: string;
  lastModifiedOn?: string;
  entityType?: string;
  entityId: string | number;
  action: string;
  createdBy?: string;
  clientFirstName?: string;
  clientLastName?: string;
  previousData?: string;
  externalClientId?: string;
  reason?: string;
}

export interface AuditLogsApiResponse {
  success: boolean;
  message?: string;
  data?: AuditLog[];
}

async function fetchWithAuth<T>(url: string): Promise<T> {
  try {
    const res = await authFetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    let data: unknown;

    try {
      data = await res.json();
    } catch {
      if (res.ok) {
        return { success: true } as unknown as T;
      }

      return {
        success: false,
        message: `Request failed: ${res.status} ${res.statusText}`,
      } as unknown as T;
    }

    if (!res.ok) {
      const message =
        typeof data === 'object' && data !== null && 'message' in data
          ? String((data as { message?: unknown }).message ?? '')
          : '';
      return {
        success: false,
        message: message || `Request failed: ${res.status} ${res.statusText}`,
      } as unknown as T;
    }

    return (Array.isArray(data) ? { success: true, data } : data) as T;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'A network error occurred.';
    return { success: false, message } as unknown as T;
  }
}

export async function getAuditLogs(entityId: string | number): Promise<AuditLogsApiResponse> {
  const url = `${apiBaseUrl}/audit/${encodeURIComponent(String(entityId))}`;
  return fetchWithAuth<AuditLogsApiResponse>(url);
}

export async function getRecentAuditLogs(): Promise<AuditLogsApiResponse> {
  const url = `${apiBaseUrl}/audit`;
  return fetchWithAuth<AuditLogsApiResponse>(url);
}

export interface GetAuditLogsParams {
  entityType?: string;
  action?: string;
  externalClientId?: string;
  createdBy?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  size?: number;
}

export interface AuditLogsPageData {
  logs: AuditLog[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface AuditLogsPagedResponse {
  success: boolean;
  message?: string;
  dateTime?: string;
  data?: AuditLogsPageData;
}

export type AuditLogsScope = 'all' | 'transactions' | 'contact';

function buildAuditLogsPath(scope: AuditLogsScope): string {
  switch (scope) {
    case 'transactions':
      return 'audit-logs/wallet-updates';
    case 'contact':
      return 'audit-logs/contact-changes';
    case 'all':
    default:
      return 'audit-logs';
  }
}

function toUtcAuditDateFilters(startDate?: string, endDate?: string) {
  const normalizedStartDate = startDate ?? '';
  let normalizedEndDate = endDate ?? '';
  if (normalizedEndDate) {
    const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(normalizedEndDate);
    if (isValidDate) {
      const [year, month, day] = normalizedEndDate.split('-').map(Number);
      const utcEndExclusive = new Date(Date.UTC(year, month - 1, day + 1, 0, 0, 0, 0));
      const endYear = utcEndExclusive.getUTCFullYear();
      const endMonth = String(utcEndExclusive.getUTCMonth() + 1).padStart(2, '0');
      const endDay = String(utcEndExclusive.getUTCDate()).padStart(2, '0');
      normalizedEndDate = `${endYear}-${endMonth}-${endDay}`;
    }
  }
  return { startDate: normalizedStartDate, endDate: normalizedEndDate };
}

export async function getAuditLogsPagedByScope(
  scope: AuditLogsScope,
  params: GetAuditLogsParams = {}
): Promise<AuditLogsPagedResponse> {
  const query = new URLSearchParams();
  if (params.entityType?.trim()) query.set('entityType', params.entityType.trim());
  if (params.action?.trim()) query.set('action', params.action.trim());
  if (params.externalClientId?.trim()) query.set('externalClientId', params.externalClientId.trim());
  if (params.createdBy?.trim()) query.set('createdBy', params.createdBy.trim());
  const { startDate: utcStart, endDate: utcEnd } = toUtcAuditDateFilters(params.startDate, params.endDate);
  if (utcStart) query.set('startDate', utcStart);
  if (utcEnd) query.set('endDate', utcEnd);
  query.set('page', String(params.page ?? 0));
  query.set('size', String(params.size ?? 10));

  const url = `${apiBaseUrl}/${buildAuditLogsPath(scope)}?${query.toString()}`;
  return fetchWithAuth<AuditLogsPagedResponse>(url);
}

export async function getAuditLogsPaged(params: GetAuditLogsParams = {}): Promise<AuditLogsPagedResponse> {
  return getAuditLogsPagedByScope('all', params);
}

export interface AuditLogDetail {
  id: number;
  action: string;
  entityType: string;
  externalClientId: string;
  clientFirstName: string;
  clientLastName: string;
  oldValue: string;
  newValue: string;
  reason: string;
  createdBy: string;
  createdOn: string;
  lastModifiedBy: string;
  lastModifiedOn: string;
}

export interface AuditLogDetailResponse {
  success: boolean;
  message?: string;
  dateTime?: string;
  data?: AuditLogDetail;
}

export async function getAuditLogDetail(id: number): Promise<AuditLogDetailResponse> {
  const url = `${apiBaseUrl}/audit-logs/${encodeURIComponent(String(id))}`;
  return fetchWithAuth<AuditLogDetailResponse>(url);
}

